import { useState, useRef, useEffect } from 'react';
import { Save, Eye, EyeOff, Key, Zap, AlertCircle, CheckCircle, Brain } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';

export function APIConfig() {
  const { showToast } = useToast();
  const [showSecrets, setShowSecrets] = useState(false);
  const testTimerRef = useRef<number | null>(null);
  const [formData, setFormData] = useState({
    openai_api_key: 'sk-proj-xxxxxxxxxxxxxxxx',
    openai_model: 'gpt-4-turbo-preview',
    openai_temperature: '0.7',
    openai_max_tokens: '2048',
    openai_rate_limit: '60',
    anthropic_api_key: 'sk-ant-xxxxxxxxxxxxxxxx',
    anthropic_model: 'claude-haiku-4-5-20251001',
    anthropic_max_tokens: '4096',
  });

  const [testResults, setTestResults] = useState<Record<string, 'success' | 'error' | null>>({
    openai: null,
    anthropic: null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    showToast('success', 'Configuration Saved', 'API configuration saved successfully');
  };

  const testConnection = (provider: 'openai' | 'anthropic') => {
    // Simulate API test
    if (testTimerRef.current) {
      clearTimeout(testTimerRef.current);
    }
    testTimerRef.current = window.setTimeout(() => {
      setTestResults({ ...testResults, [provider]: 'success' });
    }, 1000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (testTimerRef.current) {
        clearTimeout(testTimerRef.current);
      }
    };
  }, []);

  const apiProviders = [
    {
      id: 'openai',
      name: 'OpenAI',
      icon: Brain,
      description: 'GPT models for question generation',
      color: 'emerald',
      fields: [
        {
          label: 'API Key',
          name: 'openai_api_key',
          type: 'password',
          placeholder: 'sk-proj-...',
          help: 'Your OpenAI API key from platform.openai.com',
        },
        {
          label: 'Model',
          name: 'openai_model',
          type: 'select',
          options: [
            { value: 'gpt-4-turbo-preview', label: 'GPT-4 Turbo (Recommended)' },
            { value: 'gpt-4', label: 'GPT-4' },
            { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Faster, cheaper)' },
          ],
          help: 'Model used for generation',
        },
        {
          label: 'Temperature',
          name: 'openai_temperature',
          type: 'range',
          min: '0',
          max: '1',
          step: '0.1',
          help: 'Creativity level (0 = deterministic, 1 = creative)',
        },
        {
          label: 'Max Tokens',
          name: 'openai_max_tokens',
          type: 'number',
          help: 'Maximum response length',
        },
        {
          label: 'Rate Limit',
          name: 'openai_rate_limit',
          type: 'number',
          suffix: 'requests/min',
          help: 'Maximum requests per minute',
        },
      ],
    },
    {
      id: 'anthropic',
      name: 'Anthropic Claude',
      icon: Zap,
      description: 'Claude models for advanced reasoning',
      color: 'purple',
      fields: [
        {
          label: 'API Key',
          name: 'anthropic_api_key',
          type: 'password',
          placeholder: 'sk-ant-...',
          help: 'Your Anthropic API key from console.anthropic.com',
        },
        {
          label: 'Model',
          name: 'anthropic_model',
          type: 'select',
          options: [
            { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus (Most capable)' },
            { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet (Balanced)' },
            { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku (Fastest)' },
          ],
          help: 'Claude model version',
        },
        {
          label: 'Max Tokens',
          name: 'anthropic_max_tokens',
          type: 'number',
          help: 'Maximum response length',
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">API Configuration</h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure AI model providers for question generation
          </p>
        </div>
        <button
          onClick={() => setShowSecrets(!showSecrets)}
          className="btn-secondary flex items-center text-sm"
        >
          {showSecrets ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
          {showSecrets ? 'Hide' : 'Show'} API Keys
        </button>
      </div>

      {/* Security Notice */}
      <div className="card bg-amber-50 border-amber-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-900 mb-1">Security Notice</p>
            <p className="text-amber-700">
              API keys are sensitive credentials. Never share them publicly or commit them to version
              control. Keys are encrypted before storage.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* API Provider Cards */}
        {apiProviders.map((provider) => {
          const Icon = provider.icon;
          const testResult = testResults[provider.id as keyof typeof testResults];

          return (
            <div key={provider.id} className="card">
              {/* Provider Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 bg-${provider.color}-100 rounded-xl`}>
                    <Icon className={`w-6 h-6 text-${provider.color}-600`} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{provider.name}</h2>
                    <p className="text-sm text-gray-600">{provider.description}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => testConnection(provider.id as 'openai' | 'anthropic')}
                  className="btn-secondary flex items-center text-sm"
                >
                  {testResult === 'success' ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      Connected
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </button>
              </div>

              {/* Fields */}
              <div className="space-y-4">
                {provider.fields.map((field) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}
                    </label>

                    {field.type === 'password' && (
                      <div className="relative">
                        <input
                          type={showSecrets ? 'text' : 'password'}
                          value={formData[field.name as keyof typeof formData]}
                          onChange={(e) =>
                            setFormData({ ...formData, [field.name]: e.target.value })
                          }
                          className="input font-mono text-sm pr-10"
                          placeholder={field.placeholder}
                        />
                        <Key className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                      </div>
                    )}

                    {field.type === 'select' && (
                      <select
                        value={formData[field.name as keyof typeof formData]}
                        onChange={(e) =>
                          setFormData({ ...formData, [field.name]: e.target.value })
                        }
                        className="input"
                      >
                        {field.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {field.type === 'number' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={formData[field.name as keyof typeof formData]}
                          onChange={(e) =>
                            setFormData({ ...formData, [field.name]: e.target.value })
                          }
                          className="input"
                        />
                        {field.suffix && (
                          <span className="text-sm text-gray-500 whitespace-nowrap">
                            {field.suffix}
                          </span>
                        )}
                      </div>
                    )}

                    {field.type === 'range' && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <input
                            type="range"
                            min={field.min}
                            max={field.max}
                            step={field.step}
                            value={formData[field.name as keyof typeof formData]}
                            onChange={(e) =>
                              setFormData({ ...formData, [field.name]: e.target.value })
                            }
                            className="flex-1 mr-4"
                          />
                          <span className="text-sm font-mono font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded">
                            {formData[field.name as keyof typeof formData]}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Deterministic</span>
                          <span>Creative</span>
                        </div>
                      </div>
                    )}

                    {field.help && <p className="text-xs text-gray-500 mt-1">{field.help}</p>}
                  </div>
                ))}
              </div>

              {/* Connection Status */}
              {testResult === 'success' && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm text-green-800">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Connection successful</span>
                    <span className="text-green-600">• API key is valid</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Save Button */}
        <div className="flex items-center justify-end pt-6 border-t space-x-3">
          <button type="button" className="btn-secondary">
            Reset to Defaults
          </button>
          <button type="submit" className="btn-primary flex items-center">
            <Save className="w-4 h-4 mr-2" />
            Save API Configuration
          </button>
        </div>
      </form>
    </div>
  );
}
